import { gptStore } from "@/store/gptStore";
import { isCustomDoc } from "@/ts/guards";
import type { SlotDiscoveryModule } from "@/ts/module";
import type {
	CustomDoc,
	DisplaySlotConfig,
	Exactly,
	GPTStore,
	SlotsTemplate,
} from "@/ts/types";

const PAGE_AD_UNIT_PATH_TIMEOUT_MS = 2000;

export class SlotDiscovery
	implements Exactly<SlotDiscoveryModule, SlotDiscovery>
{
	name = "SlotDiscovery" as const;

	async init(): Promise<void> {
		// nothing to do here
	}

	async run(commandId: number, doc: CustomDoc): Promise<void> {
		if (!isCustomDoc(doc)) {
			return;
		}

		const { usePageAdUnitPathForCarousel, slotsTemplate, slotSizes, actions } =
			gptStore.getState();

		const adContainers = Array.from(
			doc.querySelectorAll('div[id^="div-gpt-ad-"]'),
		);
		const adUnitPathBySlot = await resolveAdUnitPathBySlot(
			adContainers,
			usePageAdUnitPathForCarousel,
		);

		const slotConfigs = generateSlotConfig({
			adContainers,
			adUnitPathBySlot,
			slotSizes,
			templateTargeting: slotsTemplate.targeting,
		});
		actions.setSlotConfigsByCommandId(commandId, slotConfigs);
	}
}

/** resolveAdUnitPathBySlot
 * resolves Record<SlotID:string, AdUnitPath:string>
 * A slot that resolves to no path is dropped (warned):
 *   an auction/GPT request against an empty path is a bad request.
 */
const resolveAdUnitPathBySlot = async (
	adContainers: Element[],
	usePageAdUnitPathForCarousel: boolean,
): Promise<Record<string, string>> => {
	// The page-data path arrives async, so wait when we prefer it (usePageAdUnitPathForCarousel);
	//   else use it as fallback if slot attr adUnitPaths are missing.
	const pageAdUnitPath = usePageAdUnitPathForCarousel
		? await waitForPageAdUnitPath()
		: gptStore.getState().pageAdUnitPath;

	// flatMap drops the [], like .map().filter() in a single pass.
	const entries = adContainers.flatMap((elem) => {
		// Reach requires carousel slots use the page's adUnitPath over their own
		// data-ad-unit-path; elsewhere the slot's own wins, with page-data fallback.
		const adUnitPath = usePageAdUnitPathForCarousel
			? pageAdUnitPath
			: (readAdUnitPathAttr(elem) ?? pageAdUnitPath);

		if (!adUnitPath) {
			console.warn(
				`[SlotDiscovery] No adUnitPath for slot "${elem.id}"; dropping slot.`,
			);
			return [];
		}
		return [[elem.id, adUnitPath] as const];
	});

	return Object.fromEntries(entries);
};

// Resolve once pageAdUnitPath is non-empty (already set -> immediately, else on
// the first non-empty value), or with "" after a timeout so a missing "page
// data" never hangs the flow. The caller decides what an empty path means.
const waitForPageAdUnitPath = (): Promise<string> => {
	const { pageAdUnitPath } = gptStore.getState();
	if (pageAdUnitPath) {
		return Promise.resolve(pageAdUnitPath);
	}

	const [nextNonEmptyPath, unsubscribe] = subscribeForNonEmptyPath();
	const [fallbackAfterTimeout, cancelTimeout] = emptyPathAfterTimeout();

	return Promise.race([nextNonEmptyPath, fallbackAfterTimeout]).finally(() => {
		unsubscribe();
		cancelTimeout();
	});
};

// A promise resolving when a non-empty pageAdUnitPath is written to store.
// Paired with its own unsubscribe so the loser of the race can be torn down.
const subscribeForNonEmptyPath = (): [Promise<string>, () => void] => {
	const { promise, resolve } = Promise.withResolvers<string>();
	const unsubscribe = gptStore.subscribe(
		(state) => state.pageAdUnitPath,
		(path) => path && resolve(path),
	);
	return [promise, unsubscribe];
};

// A promise resolving with the current pageAdUnitPath after a timeout, paired
// with its own clear so the loser of the race can be torn down.
const emptyPathAfterTimeout = (): [Promise<string>, () => void] => {
	const { promise, resolve } = Promise.withResolvers<string>();
	const timeoutId = setTimeout(
		() => resolve(gptStore.getState().pageAdUnitPath),
		PAGE_AD_UNIT_PATH_TIMEOUT_MS,
	);
	return [promise, () => clearTimeout(timeoutId)];
};

const readAdUnitPathAttr = (elem: Element) =>
	elem.getAttribute("data-ad-unit-path");

const readTargetingAttr = (elem: Element) => {
	const dataTargeting = elem.getAttribute("data-targeting");
	if (dataTargeting) {
		return JSON.parse(dataTargeting);
	}
};

interface GenerateSlotConfigParams {
	adContainers: Element[];
	adUnitPathBySlot: Record<string, string>;
	slotSizes: GPTStore["slotSizes"];
	templateTargeting: SlotsTemplate["targeting"];
}

const generateSlotConfig = ({
	adContainers,
	adUnitPathBySlot,
	slotSizes,
	templateTargeting,
}: GenerateSlotConfigParams): DisplaySlotConfig[] =>
	adContainers
		.filter((container) => container.id in adUnitPathBySlot)
		.map((container) => {
			const slotId = container.id;
			const containerTargeting = readTargetingAttr(container);

			return {
				container,
				slotId,
				size: slotSizes[slotId],
				adUnitPath: adUnitPathBySlot[slotId],
				targeting: {
					...templateTargeting,
					index: containerTargeting?.index.toString(),
					slotName: slotId,
				},
			};
		});
